"use client";

import React, { useEffect, useRef } from "react";
import { ReactTyped } from "react-typed";
import { useTranslation } from "../../../../i18n/client";
import Button from "./Button";
import NavigationButton from "./NavigationButton";
import omtoIntroVideo from "../../../../../public/images/New-landing/omto_intro.mp4";

interface HeroProps {
  lng: string;
}

const Hero: React.FC<HeroProps> = ({ lng }) => {
  const { t }: { t: any } = useTranslation(lng, "new-landing");
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const ensureVideoPlayback = async () => {
      try {
        if (video.paused) {
          await video.play();
        }
      } catch (error) {
        // Video autoplay failed, continue silently
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        ensureVideoPlayback();
      }
    };

    const handleVideoError = () => {
      // Video playback error, attempting to reload
      video.load();
      setTimeout(ensureVideoPlayback, 100);
    };

    const handleVideoEnded = () => {
      video.currentTime = 0;
      ensureVideoPlayback();
    };

    const handleVideoPause = () => {
      if (document.visibilityState === 'visible') {
        setTimeout(ensureVideoPlayback, 100);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    video.addEventListener('error', handleVideoError);
    video.addEventListener('ended', handleVideoEnded);
    video.addEventListener('pause', handleVideoPause);

    ensureVideoPlayback();

    // eslint-disable-next-line consistent-return
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      video.removeEventListener('error', handleVideoError);
      video.removeEventListener('ended', handleVideoEnded);
      video.removeEventListener('pause', handleVideoPause);
    };
  }, []);

  return (
    <section className="relative w-full h-screen overflow-hidden">
      <div className="absolute inset-0 w-full h-full">
        <video
          ref={videoRef}
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover"
        >
          <source src={omtoIntroVideo} type="video/mp4" />
        </video>
      </div>

      <div className="absolute inset-0 hero-overlay" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 h-full flex flex-col justify-center pt-[80px]">
        <div className="text-center my-auto">
          <div className="space-y-6 max-w-[90%] xl:max-w-[1100px] mx-auto mb-[140px] text-left">
            <h1 className="text-white font-space-grotesk font-bold text-[32px] md:text-[56px] hero-text-mobile leading-tight tracking-wide">
              {t("hero-title")} <br className="block md:hidden" />
              <ReactTyped
                className="text-white inline bg-blue-overlay"
                strings={t("hero-typed-strings", { returnObjects: true }) as string[]}
                typeSpeed={80}
                backSpeed={50}
                backDelay={1500}
                loop
              />
            </h1>
            <p className="text-white font-montserrat font-bold text-[18px] md:text-[26px] leading-[1.4] hero-subtitle-mobile tracking-wide text-left">
              {t("hero-description")}
            </p>
          </div>

          <Button variant="secondary" size="lg" href="#contact">
            {t("hero-cta")}
          </Button>
        </div>
        <div className="my-4 flex justify-center">
          <NavigationButton
            text={t("hero-navigation")}
            targetId="about"
            direction="down"
            textClassName="text-white font-extrabold"
            isFirstSection
          />
        </div>
      </div>
    </section>
  );
};

export default Hero;
